import type { MouseEvent, ReactElement, ReactNode } from 'react'
import type { Destination } from './ui/Controls'
import { PillNav } from './ui/Controls'

/**
 * The 68px bar the whole product sits under: the mark, where you can go, when
 * the figures were last refreshed, and who you are.
 *
 * `action` is the primary control the design puts beside the avatar. It is a
 * prop rather than a fixed button because what belongs there is a product
 * decision, and a button that does nothing is worse than an empty slot.
 *
 * The destinations and that action share a wrapper so the phone layout can move
 * both to the bottom of the screen in one piece. The design puts the primary
 * button in the tab bar there, and a button that stays at the top while the
 * navigation leaves is the one control a thumb cannot reach.
 */
export function AppBar({
  destinations,
  current,
  onNavigate,
  updated,
  action,
  who,
}: {
  destinations: readonly Destination[]
  current: string
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, to: string) => void
  /** Said plainly, or not at all — never a guess at freshness. */
  updated?: string
  action?: ReactNode
  who?: ReactNode
}): ReactElement {
  return (
    <header className="nw-appbar">
      <div className="nw-appbar-inner">
        <span className="nw-mark">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="22" height="22" rx="7" fill="var(--nw-accent)" />
            <path
              d="M6.5 15.5 L10 10.5 L13.5 13 L17.5 7.5"
              stroke="var(--nw-accent-ink)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Networthy</span>
        </span>
        <div className="nw-appbar-go">
          <PillNav
            destinations={destinations}
            current={current}
            onNavigate={onNavigate}
            label="Sections"
          />
          <div className="nw-appbar-lead">
            {updated !== undefined && <span className="nw-updated">{updated}</span>}
            {action}
          </div>
        </div>
        <div className="nw-appbar-end">{who}</div>
      </div>
    </header>
  )
}
